import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildFaqSchema, buildServiceSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const serviceSchema = buildServiceSchema(
  'Réparation de fenêtres aluminium à Paris 15ᵉ',
  'Réparation fenêtres et menuiseries aluminium dans le 15ᵉ : quincaillerie, joints, réglage de vantail.'
);

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Fenêtres Aluminium', item: 'https://fenetre-paris15.fr/reparation-fenetre-aluminium-paris-15/' },
]);

const faqs = [
  { q: 'Pourquoi ma fenêtre aluminium est-elle devenue dure à ouvrir ?', a: 'Avec le temps, la quincaillerie aluminium peut se gripper, surtout si les profilés ont subi des micro-déformations. Un nettoyage et graissage des mécanismes, couplé à un réglage des paumelles, suffit souvent à retrouver une ouverture fluide.' },
  { q: 'Les profilés aluminium sont-ils réparables ?', a: 'Les profilés eux-mêmes sont difficilement réparables en cas de choc important. En revanche, toute la quincaillerie (crémones, paumelles, galets, joints) se remplace facilement. C\'est notre spécialité.' },
  { q: 'Intervenez-vous sur les menuiseries aluminium à rupture de pont thermique ?', a: 'Oui, nous maîtrisons parfaitement les menuiseries aluminium modernes à rupture de pont thermique, qui équipent de nombreux immeubles récents du 15ᵉ, notamment à Javel, Front de Seine et Convention.' },
];

export default function AluPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <SEOHead
        title="Réparation Fenêtre Aluminium Paris 15 (75015) | DF PRO"
        description="Réparation fenêtres et menuiseries aluminium dans le 15ᵉ : quincaillerie, joints, réglage de vantail. Devis gratuit 24/48h. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/reparation-fenetre-aluminium-paris-15/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema, buildFaqSchema(faqs), serviceSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link>
            <span className="mx-2">/</span>
            <span className="text-brand-accent font-medium">Fenêtres Aluminium</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark leading-tight mb-4">Réparation de fenêtres aluminium à Paris 15ᵉ</h1>
              <p className="text-brand-text/70 text-base leading-relaxed mb-6">
                Vos menuiseries aluminium perdent en fluidité, la quincaillerie grince ou le joint ne fait plus son office ? DF PRO répare vos <strong>fenêtres et portes-fenêtres aluminium</strong> dans tout le 15ᵉ. Des profilés fins aux baies coulissantes contemporaines, nous maîtrisons toutes les configurations.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="/images/dfpro-alu-hero.webp" alt="Fenêtre aluminium contemporaine — DF PRO réparation Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-3">Spécificités des menuiseries aluminium</h2>
          <p className="text-brand-text/70 leading-relaxed mb-8 max-w-3xl">
            L'aluminium permet des profilés très fins et de grandes surfaces vitrées, très appréciés dans l'architecture contemporaine du 15ᵉ (Front de Seine, immeubles récents de Vaugirard, résidences Javel). Mais cette finesse mécanique exige une quincaillerie de précision. Avec le temps, les micro-déformations, la dilatation thermique et l'usure des pièces mobiles altèrent le fonctionnement. Nos techniciens sont formés aux spécificités des menuiseries aluminium, y compris à rupture de pont thermique.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {[
              { title: 'Vantail désaligné', desc: 'L\'ouvrant ne plaque plus uniformément contre le dormant. Problème de réglage des paumelles ou de compas fatigué. Un réglage de précision restaurera l\'étanchéité.' },
              { title: 'Quincaillerie grippée', desc: 'Mécanisme dur, poignée résistante. Les pièces aluminium/acier peuvent se corroder par couple galvanique. Nettoyage, dégrippage et lubrification résolvent la plupart des cas.' },
              { title: 'Joint d\'étanchéité HS', desc: 'Le joint EPDM se décolle ou se rétracte avec le temps. Infiltrations d\'air, sifflements, perte d\'isolation phonique. Remplacement du joint périphérique.' },
              { title: 'Infiltration d\'eau', desc: 'Souvent liée à un défaut de compression (réglage) ou un joint défaillant. Nous identifions l\'origine précise et intervenons sur le mécanisme ou l\'étanchéité.' },
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 md:p-6">
                <h3 className="font-heading font-bold text-brand-dark text-base mb-2">{item.title}</h3>
                <p className="text-brand-text/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <div className="bg-white rounded-xl p-6 md:p-8 border border-brand-mint">
              <h2 className="font-heading font-bold text-brand-dark text-xl mb-4">Ce que nous réparons</h2>
              <ul className="space-y-2 text-brand-text/70 text-sm">
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Crémones, tringleries et verrouillage multipoints</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Poignées design aluminium (adaptation et remplacement)</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Paumelles, compas et articulations</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Galets de roulement pour coulissants aluminium</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Joints d'étanchéité centraux et périphériques</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Réglage de compression et d'équerrage</li>
              </ul>
              <p className="text-brand-text/50 text-xs italic mt-4">Hors périmètre : remplacement du vitrage.</p>
            </div>
            <div className="bg-gradient-to-br from-brand-gradientFrom to-brand-gradientTo rounded-xl p-6 md:p-8 text-white">
              <h2 className="font-heading font-bold text-xl mb-4">Intervention dans le 15ᵉ</h2>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                DF PRO connaît les spécificités architecturales de chaque quartier. Les menuiseries aluminium sont particulièrement présentes dans les immeubles de bureaux et résidences récentes : Front de Seine, Beaugrenelle, Javel, et le long des grands axes rénovés. Nous intervenons sous 24 à 48h avec un devis gratuit.
              </p>
              <Link to="/devis-fenetre-paris-15/" className="inline-flex items-center gap-2 bg-white text-brand-dark font-semibold rounded-full px-6 py-3 text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
                <i className="ri-file-text-line"></i> Devis gratuit
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-8 text-center">Questions fréquentes — Fenêtres Aluminium</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-brand-mint/30 transition-colors">
                  <span className="font-heading font-bold text-brand-dark text-sm pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-brand-accent text-lg flex-shrink-0`}></i>
                </button>
                {openFaq === idx && <div className="px-5 pb-4"><p className="text-brand-text/70 text-sm">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-4">Votre fenêtre alu a besoin d'un réglage ?</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
            <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline">Zones d'intervention</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/blog/double-vitrage-embue-que-faire/" className="text-brand-accent hover:underline">Article : double vitrage embué</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/reparation-fenetre-pvc-paris-15/" className="text-brand-accent hover:underline">Fenêtres PVC</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}